import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Star, Truck, ShieldCheck, RotateCcw, Check, Minus, Plus, Home as HomeIcon, BookOpen, ArrowRight, Camera, PackageOpen, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, addRecentlyViewed } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useOrdersPaused } from "@/components/SiteClosedGate";
import ProductCard from "@/components/ProductCard";
import ProductReviews from "@/components/ProductReviews";
import RecentlyViewed from "@/components/RecentlyViewed";
import { MprtComparison } from "@/components/MprtComparison";
import Seo, { SITE_URL, COLLECTION_NAMES } from "@/components/Seo";
import { getGuide } from "@/data/guides";

const GUIDE_BY_COLLECTION = {
  "myson-trvs": "how-does-a-trv-work",
  "myson-manual-radiator-valves": "trv-vs-manual-radiator-valve",
  "myson-thermostats": "how-to-replace-a-myson-thermostat",
  "myson-mpe-valves": "what-is-an-mpe-valve",
  "bleed-vents": "radiator-not-heating-up",
};

export default function Product() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const paused = useOrdersPaused();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [reviewSummary, setReviewSummary] = useState({ count: 0, average: 0 });
  const [showSticky, setShowSticky] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const buyRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setActiveImg(0);
    setReviewSummary({ count: 0, average: 0 });
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      if (r.data.review_summary) setReviewSummary(r.data.review_summary);
      if (r.data.product) addRecentlyViewed(r.data.product.slug);
    }).catch(() => setData("error"));
  }, [slug]);

  useEffect(() => {
    const el = buyRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [data]);

  if (data === null) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-muted-foreground">Loading…</div>;
  if (data === "error") return <div className="max-w-7xl mx-auto px-4 py-20 text-center">Product not found. <Link to="/" className="text-primary underline">Go home</Link></div>;

  const p = data.product;
  const outOfStock = p.stock === 0;
  const hasSpecs = Object.keys(p.specs || {}).length > 0;
  const hasReviews = reviewSummary.count > 0;

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: p.name,
    image: p.images && p.images.length ? p.images.map((im) => (im.startsWith("http") ? im : `${SITE_URL}${im}`)) : [p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image}`],
    description: p.description,
    sku: p.sku,
    mpn: p.sku,
    brand: { "@type": "Brand", name: p.specs?.Brand || p.brand || "Myson" },
    ...(hasReviews && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: reviewSummary.average,
        reviewCount: reviewSummary.count,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    offers: {
      "@type": "Offer",
      priceCurrency: "GBP",
      price: p.price,
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      itemCondition: "https://schema.org/NewCondition",
      availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: `${SITE_URL}/products/${p.slug}`,
      seller: { "@type": "Organization", name: "Hydronic Heating Controls & Valves" },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "GBP" },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" },
        },
      },
      ...(!p.bargain && {
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "GB",
          returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 30,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/ReturnShippingFees",
        },
      }),
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: COLLECTION_NAMES[p.collection_slug] || p.collection_slug.replace(/-/g, " "), item: `${SITE_URL}/collections/${p.collection_slug}` },
      { "@type": "ListItem", position: 3, name: p.name, item: `${SITE_URL}/products/${p.slug}` },
    ],
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Seo
        title={p.name}
        description={p.description}
        image={p.image}
        jsonLd={[jsonLd, breadcrumbLd]}
      />
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
        <span>/</span>
        <Link to={`/collections/${p.collection_slug}`} className="hover:text-primary capitalize">{p.collection_slug.replace(/-/g, " ")}</Link>
        <span>/</span>
        <span className="text-foreground font-medium line-clamp-1">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* GALLERY */}
        <div className="lg:sticky lg:top-28 self-start">
          {(() => {
            const gallery = p.images && p.images.length ? p.images : [p.image];
            const current = gallery[Math.min(activeImg, gallery.length - 1)];
            return (
              <>
                <div className="aspect-square rounded-sm overflow-hidden border border-border bg-white" data-testid="product-image">
                  <img src={current} alt={p.name} className={`${p.compact_image ? `w-full h-full object-contain ${p.img_pad || (p.extra_compact ? "p-28" : "p-12")}` : "w-full h-full object-cover"}${p.rotate ? " rotate-90" : ""}`} />
                </div>
                {gallery.length > 1 && (
                  <div className="mt-3 grid grid-cols-4 gap-3" data-testid="product-thumbnails">
                    {gallery.map((src, i) => (
                      <button key={i} onClick={() => setActiveImg(i)} data-testid={`product-thumb-${i}`}
                        className={`aspect-square rounded-sm overflow-hidden border bg-white transition-colors ${i === activeImg ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/50"}`}>
                        <img src={src} alt={`${p.name} view ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                {p.bargain && gallery.length > 1 && (
                  <p className="mt-2 text-xs text-muted-foreground text-center">Photo 1: the item &middot; Photo 2: the actual packaging damage</p>
                )}
              </>
            );
          })()}
          {!p.real_image && (
            <p className="mt-3 text-xs text-muted-foreground text-center">Placeholder image — final product photography coming soon.</p>
          )}
        </div>

        {/* DETAILS */}
        <div>
          <span className="text-overline text-primary">{p.specs?.Brand}</span>
          <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-display font-black tracking-tight" data-testid="product-title">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3 text-sm">
            {hasReviews ? (
              <a href="#product-reviews" className="flex items-center gap-1 hover:text-primary" data-testid="rating-badge">
                <Star className="w-4 h-4 fill-accent text-accent" /> {reviewSummary.average.toFixed(1)} ({reviewSummary.count} review{reviewSummary.count === 1 ? "" : "s"})
              </a>
            ) : (
              <a href="#product-reviews" className="text-muted-foreground hover:text-primary" data-testid="rating-badge">No reviews yet</a>
            )}
            <span className="text-muted-foreground font-mono text-xs">SKU: {p.sku}</span>
          </div>

          <div className="mt-6 flex items-end gap-3 flex-wrap">
            {p.special_offer && (
              <span className="w-full text-overline text-accent font-bold" data-testid="special-offer-label">{p.was_price ? "Below RRP" : "Special Offer"}</span>
            )}
            <span className="text-4xl font-display font-black text-foreground" data-testid="product-price">{gbp(p.price)}</span>
            {p.was_price && <span className="text-xl text-muted-foreground pb-1" data-testid="product-was-price">RRP <span className="line-through">{gbp(p.was_price)}</span></span>}
            {p.was_price && p.was_price > p.price && (
              <span className="pb-1 text-sm font-semibold text-accent">Save {gbp(p.was_price - p.price)} off RRP</span>
            )}
          </div>

          {p.special_offer && p.offer_ends && (
            <p className="mt-2 text-sm font-medium text-accent" data-testid="offer-ends">Special offer ends {p.offer_ends}</p>
          )}

          <div className="mt-4 flex items-center gap-2 text-sm font-medium" data-testid="stock-status">
            {outOfStock
              ? <span className="text-destructive flex items-center gap-1.5">Out of stock{p.stock_note ? ` — ${p.stock_note.charAt(0).toLowerCase()}${p.stock_note.slice(1)}` : ""}</span>
              : <span className="text-primary flex items-center gap-1.5"><Check className="w-4 h-4" /> In stock — {p.stock} available</span>}
          </div>

          <div className="mt-6 flex items-center gap-4" ref={buyRef}>
            <div className="flex items-center border border-border rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-muted" data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
              <span className="w-12 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-muted" data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              data-testid="add-to-cart-detail"
              disabled={outOfStock || paused}
              onClick={() => { addItem(p, qty); toast.success(`${qty} × added to basket`); }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white py-3.5 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ShoppingCart className="w-5 h-5" /> {outOfStock ? "Out of Stock" : paused ? "Orders Paused" : "Add to Basket"}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[{ i: Truck, t: "Free tracked delivery" }, { i: ShieldCheck, t: p.specs?.Warranty ? `${p.specs.Warranty.split("(")[0].trim()} warranty` : "Warranty incl." }, { i: RotateCcw, t: p.bargain ? "30-day returns" : "30-day returns" }].map((x) => (
              <div key={x.t} className="border border-border rounded-sm py-3">
                <x.i className="w-5 h-5 mx-auto text-primary" />
                <span className="block text-xs mt-1.5 font-medium">{x.t}</span>
              </div>
            ))}
          </div>

          {/* NEED TO KNOW */}
          <div className="mt-6 rounded-sm border border-primary/20 bg-primary/[0.04] p-5" data-testid="need-to-know">
            <h2 className="text-overline text-primary">Need to know</h2>
            <ul className="mt-3 grid sm:grid-cols-2 gap-y-2 gap-x-5 text-sm">
              {[
                `Genuine ${p.specs?.Brand || p.brand || "Myson"}`,
                p.specs?.Connection && `${p.specs.Connection} connection`,
                p.specs?.Warranty ? `${p.specs.Warranty.split("(")[0].trim()} manufacturer warranty` : "Manufacturer warranty",
                "Free tracked delivery (Royal Mail Tracked 48)",
                outOfStock ? (p.stock_note ? `${p.stock_note} — contact us to reserve one` : "Available to order — contact us") : "In stock — ready to dispatch",
              ].filter(Boolean).map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to={`/find-replacement?product=${encodeURIComponent(p.name)}&sku=${encodeURIComponent(p.sku)}`}
              data-testid="not-sure-photo-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
              <Camera className="w-4 h-4" /> Not sure this is the correct part? Send us a photo <ArrowRight className="w-4 h-4" />
            </Link>
            {p.collection_slug === "kickspace-grills" && (
              <Link
                to="/guides/myson-fan-convector-technical-data"
                data-testid="product-technical-data-link"
                className="mt-3 flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
                <FileText className="w-4 h-4" /> View Myson fan convector technical data <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {p.bargain && (
            <div className="mt-4 rounded-sm border border-amber-300 bg-amber-50 p-5" data-testid="bargain-box">
              <h2 className="text-overline text-amber-700 flex items-center gap-2"><PackageOpen className="w-4 h-4" /> Bargain Bin — new item, damaged packaging</h2>
              <ul className="mt-3 space-y-1.5 text-sm">
                <li className="flex gap-2"><span className="font-medium w-32 shrink-0">Product</span><span>{p.condition || "New & unused"}</span></li>
                {p.packaging && <li className="flex gap-2"><span className="font-medium w-32 shrink-0">Packaging</span><span>{p.packaging}</span></li>}
                {p.bargain_reason && <li className="flex gap-2"><span className="font-medium w-32 shrink-0">Reason for discount</span><span>{p.bargain_reason}</span></li>}
                <li className="flex gap-2"><span className="font-medium w-32 shrink-0">Quantity available</span><span>{p.stock} {p.stock === 1 ? "only" : "available"}</span></li>
              </ul>
              <p className="mt-3 text-xs text-amber-900/80 leading-relaxed">
                <strong>Please note:</strong> this is brand-new, unused stock reduced solely because of the packaging described above. All Bargain Bin products retain their applicable manufacturer warranty and are covered by your statutory consumer rights and our 30-day returns policy. Damaged packaging alone isn't a fault, but if the product itself is faulty you have exactly the same rights as with any other purchase. See our <Link to="/delivery-returns" className="underline font-medium">Delivery &amp; Returns</Link> page for details.
              </p>
            </div>
          )}

          {/* TABS */}
          <div className="mt-8 border-t border-border">
            <div className="flex gap-6 border-b border-border">
              {[["desc", "Description"], ...(hasSpecs ? [["specs", "Specifications"]] : [])].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="py-5">
              {tab === "desc" || !hasSpecs ? (
                <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(p.specs || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-border/60">
                        <td className="py-2.5 font-medium w-1/3">{k}</td>
                        <td className="py-2.5 text-muted-foreground">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {(() => {
            const rg = getGuide(GUIDE_BY_COLLECTION[p.collection_slug]);
            return rg ? (
              <Link
                to={`/guides/${rg.slug}`}
                data-testid="product-related-guide"
                className="mt-6 flex items-center gap-3 border border-border rounded-sm bg-card p-4 hover:border-primary transition-colors group">
                <BookOpen className="w-6 h-6 text-primary shrink-0" />
                <div className="flex-1">
                  <span className="block text-overline text-primary">Helpful guide</span>
                  <span className="block text-sm font-semibold leading-snug group-hover:text-primary transition-colors">{rg.title}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : null;
          })()}
        </div>
      </div>

      <MprtComparison sku={p.sku} />

      <ProductReviews slug={p.slug} onSummary={(s) => s && setReviewSummary(s)} />

      {/* RELATED */}
      {data.related?.length > 0 && (
        <section className="mt-20">
          <h2 className="text-2xl font-display font-bold tracking-tight mb-6">You may also need</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {data.related.map((r, i) => <ProductCard key={r.slug} product={r} index={i} />)}
          </div>
        </section>
      )}

      <RecentlyViewed excludeSlug={p.slug} />

      {/* STICKY ADD-TO-BASKET */}
      {!outOfStock && !paused && (
        <div
          data-testid="sticky-add-bar"
          className={`fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.08)] transition-transform duration-300 ${showSticky ? "translate-y-0" : "translate-y-full"}`}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
            <img src={p.image} alt="" className="w-11 h-11 rounded-sm border border-border object-contain bg-white hidden sm:block" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{p.name}</p>
              <p className="text-sm text-primary font-display font-bold">{gbp(p.price)}</p>
            </div>
            <button
              data-testid="sticky-add-btn"
              onClick={() => { addItem(p, qty); toast.success(`${qty} × added to basket`); }}
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 transition-colors">
              <ShoppingCart className="w-5 h-5" /> Add to Basket
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
